import React, { useState } from 'react';
import StepWizard from 'react-step-wizard';
import styled from 'styled-components';

import ActivateSMS from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/ActivateSms';
import PhoneSmsVerification from './templates/PhoneSmsVerification';
import ExistSearchPhoneNumbers from './templates/SearchPhoneNumbers/ExistSearchPhoneNumbers';
import { getAgentDesktopURL } from 'src/presentation/components/pages/shared/utils/Utils';
import { ModalSize } from 'src/presentation/components/pages/shared/EmbedModal';
import NewSearchPhoneNumbers from './templates/SearchPhoneNumbers/NewSearchPhoneNumbers';
import { NumberPreference } from 'src/presentation/components/pages/channels/shared/helpers/stageConfiguration';
import { PhoneNumberCapability, PhoneNumberType } from 'src/actions/availableNumbers/availableNumbersResource';
import PhoneNumberPurchased from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/PhoneNumberPurchased';
import PortPhoneNumber from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/PortPhoneNumberSms';
import ReviewPhoneNumber from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/ReviewPhoneNumber';
import SelectPhonePreference from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/SelectPhonePreference';
import SetEntryPointSMS from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/SetEntryPointSMS';
import SetupCompletedSMS from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/SetupCompletedSMS';
import {
  StageConfig,
  getStagesList,
} from 'src/presentation/components/pages/channels/shared/helpers/stageConfiguration';
import {
  ModalContainer,
  StyledStepContainer,
} from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/shared/elements';
import PhoneSmsVerificationPortOrForward from './templates/PhoneSmsVerificationPortOrForward';

type ConfigureNumberModalModalType = {
  handleCloseModal: () => void;
};

export const initialState = {
  numberPreference: NumberPreference.new,
  activateSms: true,
  selectedPhone: {
    id: '',
    countryCode: '',
    phoneNumber: '',
    locality: '',
    region: '',
    phoneNumberType: PhoneNumberType.LOCAL,
    capabilities: [],
  },
};

const ConfigureSmsModal = ({ handleCloseModal }: ConfigureNumberModalModalType) => {
  const organizationSupportLink = getAgentDesktopURL('support.html');
  const [stageConfig, updateStageConfig] = useState(initialState);
  const [activeStep, saveWizardStep] = useState(1);

  const saveFormData = newData => {
    updateStageConfig({ ...stageConfig, ...newData });
  };

  const setInitState = () => {
    updateStageConfig(initialState);
  };

  const stagesSchema: StageConfig[] = [
    {
      stageId: 'SelectPhonePreference',
      stage: <SelectPhonePreference saveStep={saveFormData} stageConfig={stageConfig} />,
      nextStages: [
        { stageId: `NewSearchPhoneNumbers`, showStage: stageConfig.numberPreference === NumberPreference.new },
        { stageId: `ExistSearchPhoneNumbers`, showStage: stageConfig.numberPreference === NumberPreference.exists },
        { stageId: `PortPhoneNumber`, showStage: stageConfig.numberPreference === NumberPreference.port },
      ],
    },
    {
      stageId: 'NewSearchPhoneNumbers',
      stage: <NewSearchPhoneNumbers saveStep={saveFormData} organizationSupportLink={organizationSupportLink} />,
      nextStages: [{ stageId: 'ReviewPhoneNumber' }],
    },
    {
      stageId: 'ExistSearchPhoneNumbers',
      stage: <ExistSearchPhoneNumbers saveStep={saveFormData} />,
      nextStages: [{ stageId: 'SetEntryPoint' }],
    },
    {
      stageId: 'PortPhoneNumber',
      stage: <PortPhoneNumber setInitState={setInitState} />,
      nextStages: [{ stageId: 'PhoneSmsVerificationPortOrForward' }],
    },
    {
      stageId: 'PhoneSmsVerificationPortOrForward',
      stage: <PhoneSmsVerificationPortOrForward closeModal={handleCloseModal} />,
      nextStages: [],
    },
    {
      stageId: 'ReviewPhoneNumber',
      stage: <ReviewPhoneNumber phoneData={stageConfig.selectedPhone} />,
      nextStages: [{ stageId: 'PhoneNumberPurchased' }],
    },
    {
      stageId: 'PhoneNumberPurchased',
      stage: <PhoneNumberPurchased phoneNumber={stageConfig.selectedPhone.phoneNumber} />,
      nextStages: [{ stageId: 'PhoneSmsVerification-NewPhone' }],
    },
    {
      stageId: 'PhoneSmsVerification-NewPhone',
      stage: <PhoneSmsVerification stageConfig={stageConfig} />,
      nextStages: [{ stageId: 'ActivateSMS' }],
    },
    {
      stageId: 'ActivateSMS',
      stage: <ActivateSMS saveStep={saveFormData} stageConfig={stageConfig} />,
      nextStages: [
        { stageId: 'SetEntryPoint', showStage: stageConfig.activateSms },
        { stageId: 'SetupComplete', showStage: !stageConfig.activateSms },
      ],
    },
    {
      stageId: `SetEntryPoint`,
      stage: <SetEntryPointSMS phoneNumber={stageConfig.selectedPhone.phoneNumber} />,
      nextStages: [
        {
          stageId: 'PhoneSmsVerification-ExistingPhone',
          showStage: stageConfig.numberPreference === NumberPreference.exists,
        },
        { stageId: 'SetupComplete', showStage: stageConfig.numberPreference === NumberPreference.new },
      ],
    },
    {
      stageId: 'PhoneSmsVerification-ExistingPhone',
      stage: <PhoneSmsVerification stageConfig={stageConfig} />,
      nextStages: [{ stageId: 'SetupComplete' }],
    },
    {
      stageId: `SetupComplete`,
      stage: (
        <SetupCompletedSMS
          closeModal={handleCloseModal}
          selectedPhone={stageConfig.selectedPhone}
          setInitState={setInitState}
          stageConfig={stageConfig}
        />
      ),
      nextStages: [],
    },
  ];

  const stages = getStagesList(stagesSchema);

  const getFormTitle = (stage: number) => `Configure SMS – step ${stage} of ${stages.length}`;

  const handleStepChange = (stepChange: { previousStep: number; activeStep: number }) => {
    saveWizardStep(stepChange.activeStep);
  };

  const getModalContent = (
    <StyledStepWizard onStepChange={handleStepChange} initialStep={1}>
      {stages.map(({ stageId, stage }, index) => (
        <StyledStepContainer key={index} data-aid={stageId} entityType={PhoneNumberCapability.SMS}>
          {stage}
        </StyledStepContainer>
      ))}
    </StyledStepWizard>
  );

  return (
    <ModalContainer
      data-aid="configure-sms"
      title={getFormTitle(activeStep)}
      modalSize={ModalSize.LARGE}
      content={getModalContent}
      onCancel={handleCloseModal}
      disableOutsideClick={true}
    />
  );
};

const StyledStepWizard = styled(StepWizard)`
  overflow: hidden;
  text-align: left;
`;

export default ConfigureSmsModal;
